import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Building2, Handshake } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { DateCell } from "@/components/DateCell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface SalesAgreement {
  id: string;
  agreementNumber: string;
  status: string;
  commitmentType: string;
  fulfillmentType: string;
  committedValue: string | null;
  validFrom: string;
  validTo: string;
  createdAt: string;
  account?: { id: string; name: string };
}

interface SalesAgreementsResponse {
  salesAgreements: SalesAgreement[];
  total: number;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export default function SalesAgreementsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading } = useQuery<SalesAgreementsResponse>({
    queryKey: ["/api/sales-agreements", { search: searchValue, page: currentPage, limit: pageSize, status: statusFilter }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
      });
      if (searchValue) params.set("search", searchValue);
      if (statusFilter !== "all") params.set("status", statusFilter);
      return api.get<SalesAgreementsResponse>(`/sales-agreements?${params}`, sessionId);
    },
  });

  const columns = [
    {
      key: "createdAt",
      header: "Entry",
      sortable: true,
      cell: (row: SalesAgreement) => <DateCell value={row.createdAt} className="text-muted-foreground" />,
    },
    {
      key: "agreementNumber",
      header: "Agreement #",
      sortable: true,
      cell: (row: SalesAgreement) => (
        <div className="font-medium" data-testid={`text-agreement-number-${row.id}`}>{row.agreementNumber}</div>
      ),
    },
    {
      key: "account",
      header: "Account",
      cell: (row: SalesAgreement) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span>{row.account?.name || "-"}</span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row: SalesAgreement) => <StatusBadge status={row.status} type="procurement" />,
    },
    {
      key: "commitmentType",
      header: "Commitment",
      cell: (row: SalesAgreement) => (
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{row.commitmentType}</span>
      ),
    },
    {
      key: "fulfillmentType",
      header: "Fulfillment",
      cell: (row: SalesAgreement) => (
        <span className="text-xs font-medium">{row.fulfillmentType?.replace(/_/g, " ")}</span>
      ),
    },
    {
      key: "committedValue",
      header: "Committed",
      className: "text-right",
      cell: (row: SalesAgreement) => (
        <span className="font-medium">
          {row.committedValue ? `$${Number(row.committedValue).toLocaleString()}` : "-"}
        </span>
      ),
    },
    {
      key: "validFrom",
      header: "Valid From",
      cell: (row: SalesAgreement) => <DateCell value={row.validFrom} className="text-muted-foreground" />,
    },
    {
      key: "validTo",
      header: "Valid To",
      cell: (row: SalesAgreement) => <DateCell value={row.validTo} className="text-muted-foreground" />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Agreements"
        description="Standing customer orders and contract commitments"
        actions={
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search sales agreements..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-64"
            />
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.salesAgreements || []}
        totalCount={data?.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/sales-agreements/${row.id}`)}
        emptyIcon={<Handshake className="h-12 w-12 text-muted-foreground/50" />}
        emptyMessage="No sales agreements"
      />
    </div>
  );
}
